import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import MessagingPillars from '../shared/MessagingPillars';
import SemanticHRProductHeader from '../shared/SemanticHRProductHeader';
import SectionSurface from '../shared/SectionSurface';
import {
  ENTERPRISE_ECOSYSTEM,
  HERO_SOLUTION_PILLS,
} from '../../../content/enterpriseMessaging';
import SemanticHRTourVisual from '../solutions/semantic-hr/SemanticHRTourVisual';

const PILL_ACCENT = {
  brand: 'badge-brand',
  migration: 'badge-migration',
  semantic: 'badge-semantic',
  enterprise: 'badge-enterprise',
};

function HomeSemanticHRCardHeader() {
  return (
    <header className="ia-semhr-home-card-head">
      <div className="ia-semhr-home-card-band">
        <div className="ia-semhr-home-card-lockup">
          <SemanticHRProductHeader
            titleClassName="text-2xl font-semibold tracking-tight text-ink-900 sm:text-[1.7rem]"
            taglineClassName="text-sm font-medium text-brand-700 leading-snug"
            logoClassName="mt-1 h-4 w-auto object-contain sm:h-[18px]"
          />
        </div>

        <div className="ia-semhr-home-card-copy">
          <p className="ia-semhr-home-card-lead">
            Split-screen copilot, embedded BI gallery, and on-demand Cortex visualizations on governed HR
            metrics.
          </p>
          <p className="ia-semhr-home-card-meta">
            Governed semantic models · Workday &amp; SuccessFactors · Power BI embed
          </p>
        </div>
      </div>
    </header>
  );
}

/**
 * Enterprise Intelligence Ecosystem: platform narrative + Semantic HR product panel.
 */
export default function EnterpriseEcosystem() {
  return (
    <SectionSurface
      variant="grid"
      as="section"
      decor="nodes"
      bleed="both"
      flowTop
      flowBottom
      overlap
    >
      <div className="container-x section">
        <div className="relative mb-10 lg:mb-12 text-center">
          <div
            aria-hidden="true"
            className="absolute left-1/2 -top-6 h-32 w-32 -translate-x-1/2 rounded-full bg-brand-100/40 blur-3xl pointer-events-none"
          />
          <p className="eyebrow relative">{ENTERPRISE_ECOSYSTEM.eyebrow}</p>
          <h2 className="relative mx-auto mt-4 max-w-4xl text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-tight text-ink-900 leading-[1.1]">
            {ENTERPRISE_ECOSYSTEM.title}
          </h2>
          <p className="relative mx-auto mt-5 max-w-3xl text-base sm:text-lg leading-relaxed text-ink-600">
            {ENTERPRISE_ECOSYSTEM.body}
          </p>

          <MessagingPillars limit={3} className="relative mx-auto mt-8 max-w-3xl text-left" />

          <div className="relative mx-auto mt-8 max-w-3xl">
            <p className="text-xs font-medium text-ink-500 mb-2.5">Solutions we build on your stack</p>
            <div className="flex flex-wrap justify-center gap-2">
              {HERO_SOLUTION_PILLS.map((pill) => (
                <a
                  key={pill.label}
                  href={pill.href}
                  className={`${PILL_ACCENT[pill.accent] || 'badge-brand'} min-h-[24px] transition-opacity hover:opacity-90`}
                >
                  {pill.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-1/4 h-48 w-48 max-w-full rounded-full bg-accent-100/30 blur-3xl"
          />
          <div className="relative w-full min-w-0 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-lg ring-1 ring-brand-100/50">
            <HomeSemanticHRCardHeader />

            <div className="overflow-hidden border-t border-ink-200/80">
              <SemanticHRTourVisual className="rounded-none border-0 shadow-none w-full" />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-surface-subtle/50 px-5 py-3">
              <a
                href="/semantic-hr"
                className="inline-flex min-h-[24px] items-center gap-1 text-sm font-semibold text-brand-600 hover:text-brand-700"
              >
                Explore Semantic HR
                <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <a
                href="/semantic-hr#metrics"
                className="inline-flex min-h-[24px] items-center text-sm font-medium text-ink-500 hover:text-ink-700"
              >
                HR metrics catalog →
              </a>
            </div>
          </div>
        </div>
      </div>
    </SectionSurface>
  );
}
